import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import PageHeader from "../components/PageHeader";
import { LoadingState, EmptyState } from "../components/CommonStates";
import { API_ENDPOINTS } from "../config/api";
import { getResponseSortPriority } from "../config/providerModels";
import Button from "../components/Button";
import { useParams } from "react-router-dom";
import { useNavigateWithCommandClick } from "../hooks/useNavigateWithCommandClick";
import { AdminResponseTabs } from "../components/ResponseTabs";
import { Citations, processMessageContent } from "../components/Citations";
import { formatDate, formatMessageTime } from "../utils/dateFormatting";

// Styled Components (copied from Messages.js)
const MessagesContainer = styled.div`
  background: #f8f9fa;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 80px;

  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  position: relative;

  @media (max-width: 768px) {
    height: calc(100vh - 120px);
  }
`;

const MessagesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem 0 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem 0 60px 0;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0 80px 0;
  }
`;

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 60rem;
  width: 100%;
  padding: 0 1rem;

  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 70%;
  align-self: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};
  align-items: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};

  @media (max-width: 768px) {
    max-width: 85%;
  }

  @media (max-width: 480px) {
    max-width: 95%;
  }
`;

const MessageContent = styled.div`
  min-height: 50px;
  background: ${({ $type }) => {
    if ($type === "user") return "#000000";
    if ($type === "assistant") return "#e5e5e5";
    if ($type === "error") return "#ffebee";
    return "#f1f3f4";
  }};
  color: ${({ $type }) => {
    if ($type === "user") return "white";
    if ($type === "error") return "#c62828";
    return "#333";
  }};
  padding: 0.75rem;
  border-radius: 1rem;
  position: relative;
  width: fit-content;
`;

const MessageText = styled.div`
  p {
    margin: 0 0 0.5rem 0;
    line-height: 1.375;
    word-wrap: break-word;
    font-size: 1.125rem;
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: decimal;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: disc;
  }

  li {
    margin: 0.25rem 0;
    line-height: 1.4;
    word-wrap: break-word;
  }

  ol li {
    margin: 0.5rem 0;
  }

  ul li {
    margin: 0.25rem 0;
  }

  ol ol,
  ul ul,
  ol ul,
  ul ol {
    margin: 0.25rem 0;
    padding-left: 1.2rem;
  }

  strong {
    font-weight: 600;
    color: inherit;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.75rem 0 0.5rem 0;
    font-weight: 600;
    line-height: 1.3;
  }

  h1 {
    font-size: 1.5rem;
  }
  h2 {
    font-size: 1.375rem;
  }
  h3 {
    font-size: 1.25rem;
  }
`;

const MessageMetadata = styled.span`
  font-size: 0.875rem;
  opacity: 0.7;
  display: block;
  margin-top: 0.5rem;
  padding: 0 0.5rem;
  color: #666;
  white-space: nowrap;
  width: fit-content;
  align-self: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};
`;

const RatingContainer = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 0.125rem solid #e9ecef;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: fit-content;
  max-width: 100%;
  color: #6c757d;
  font-size: 0.875rem;
`;

const RatingButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const RatingButton = styled.div`
  background: ${({ $selected, $type }) => {
    if ($selected && $type === "up") return "#d4edda";
    if ($selected && $type === "down") return "#f8d7da";
    return "transparent";
  }};
  border: 0.125rem solid
    ${({ $selected, $type }) => {
      if ($selected && $type === "up") return "#28a745";
      if ($selected && $type === "down") return "#dc3545";
      return "#dee2e6";
    }};
  color: ${({ $selected, $type }) => {
    if ($selected && $type === "up") return "#155724";
    if ($selected && $type === "down") return "#721c24";
    return "#6c757d";
  }};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  height: 2rem;
  width: 3rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SavedFeedbackDisplay = styled.div`
  width: 20rem;
  min-height: 4rem;
  padding: 0.5rem;
  background: #ffffff;
  border: 0.125rem solid #e9ecef;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ErrorState = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 0.125rem solid #f5c6cb;
`;

const SidePanel = styled.div`
  width: 300px;
  background: white;
  border-left: 0.125rem solid #e0e0e0;
  padding: 1.5rem;
  overflow-y: auto;
  height: calc(100vh - 80px);
  position: fixed;
  right: 0;
  top: 80px;
  z-index: 100;

  @media (max-width: 1024px) {
    transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "100%")});
    transition: transform 0.3s ease-in-out;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 1.5rem;
  background: ${({ $highlight }) => ($highlight ? "#f8f9fa" : "transparent")};
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
`;

const ToggleButton = styled(Button)`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 101;
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MainContent = styled.div`
  margin-right: ${({ $isOpen }) => ($isOpen ? "300px" : "0")};
  transition: margin-right 0.3s ease-in-out;

  @media (max-width: 1024px) {
    margin-right: 0;
  }
`;

function AdminThread() {
  const { threadId } = useParams();
  const navigate = useNavigateWithCommandClick();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [activeTabs, setActiveTabs] = useState({}); // Track active tab for each question group
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    const fetchThread = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(API_ENDPOINTS.adminChatThread(threadId), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        const data = await response.json();
        if (response.ok) {
          setThread(data);
        } else {
          setError(data.error || "Failed to fetch thread");
        }
      } catch (err) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchThread();
  }, [threadId]);

  const handleDeleteThread = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this thread? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminChatThread(threadId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        navigate("/admin/chat-logs");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to delete thread.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Group messages by parentMessageId
  const groupMessages = useCallback(() => {
    if (!thread?.messages) return [];

    // Remove duplicates first (same ID)
    const uniqueMessages = thread.messages.reduce((acc, message) => {
      const existing = acc.find((m) => m.id === message.id);
      if (!existing) {
        acc.push(message);
      }
      return acc;
    }, []);

    const groups = [];
    const processedMessageIds = new Set();

    // Sort messages by creation time to maintain chronological order
    const sortedMessages = [...uniqueMessages].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    // First pass: Find all assistant messages that reference other assistant messages
    // and group them by their parentMessageId
    const assistantResponseGroups = new Map();

    sortedMessages.forEach((message) => {
      if (message.role === "assistant" && message.parentMessageId) {
        const parentMessage = sortedMessages.find(
          (m) => m.id === message.parentMessageId
        );

        if (parentMessage && parentMessage.role === "assistant") {
          // This is a response to another assistant message
          if (!assistantResponseGroups.has(message.parentMessageId)) {
            assistantResponseGroups.set(message.parentMessageId, []);
          }
          assistantResponseGroups.get(message.parentMessageId).push(message);
          processedMessageIds.add(message.id);
        }
      }
    });

    // Create groups for assistant message responses
    assistantResponseGroups.forEach((responses, parentId) => {
      const parentMessage = sortedMessages.find((m) => m.id === parentId);
      const allResponsesChronological = [parentMessage, ...responses];

      // The first response chronologically should be the original
      const originalResponseId = allResponsesChronological[0]?.id;

      const allResponses = allResponsesChronological.sort(
        (a, b) =>
          getResponseSortPriority(a, originalResponseId) -
          getResponseSortPriority(b, originalResponseId)
      );

      // Mark parent as processed too
      processedMessageIds.add(parentId);

      groups.push({
        id: `group_${parentId}_responses`,
        userMessage: null,
        botResponses: allResponses,
        parentResponse: parentMessage
      });
    });

    // Second pass: Handle remaining messages
    sortedMessages.forEach((message) => {
      if (processedMessageIds.has(message.id)) return;

      if (message.role === "user") {
        // Find all assistant responses to this user message
        const responsesChronological = sortedMessages.filter(
          (m) =>
            m.role === "assistant" &&
            m.parentMessageId === message.id &&
            !processedMessageIds.has(m.id)
        );

        // The first response chronologically should be the original
        const originalResponseId = responsesChronological[0]?.id;

        const responses = responsesChronological.sort(
          (a, b) =>
            getResponseSortPriority(a, originalResponseId) -
            getResponseSortPriority(b, originalResponseId)
        );

        // Mark responses as processed
        responses.forEach((r) => processedMessageIds.add(r.id));
        processedMessageIds.add(message.id);

        groups.push({
          id: `group_${message.id}`,
          userMessage: message,
          botResponses: responses
        });
      } else if (message.role === "assistant") {
        // Orphaned assistant message (no parent or parent not found)
        processedMessageIds.add(message.id);

        groups.push({
          id: `single_${message.id}`,
          userMessage: null,
          botResponses: [message]
        });
      }
    });

    // Sort groups by the earliest message timestamp to maintain chronological order
    groups.sort((a, b) => {
      const getEarliestTimestamp = (group) => {
        const allMessages = [
          ...(group.userMessage ? [group.userMessage] : []),
          ...group.botResponses
        ];
        return Math.min(
          ...allMessages.map((m) => new Date(m.createdAt).getTime())
        );
      };

      return getEarliestTimestamp(a) - getEarliestTimestamp(b);
    });

    return groups;
  }, [thread?.messages]);

  // Handle tab switching
  const handleTabSwitch = (questionGroupId, botMessageId) => {
    setActiveTabs((prev) => ({
      ...prev,
      [questionGroupId]: botMessageId
    }));
  };

  // Helper function to parse citations from raw content
  const parseContentAndCitations = (content) => {
    // Try to detect and extract JSON data at the end of the content

    // Pattern 1: Content starting with {"response": - Full JSON object at the beginning
    if (
      content.startsWith('{"response":') ||
      content.startsWith('{ "response":')
    ) {
      try {
        const fullData = JSON.parse(content);
        if (fullData.response) {
          return {
            content: fullData.response,
            citations: fullData.citations
              ? JSON.stringify(fullData.citations)
              : null
          };
        }
      } catch (e) {
        // If full JSON parse fails, try regex extraction
        const responseMatch = content.match(
          /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/
        );
        const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);

        if (responseMatch) {
          return {
            content: responseMatch[1]
              .replace(/\\\\/g, "\\")
              .replace(/\\"/g, '"')
              .replace(/\\n/g, "\n")
              .replace(/\\r/g, "\r")
              .replace(/\\t/g, "\t"),
            citations: citationsMatch ? citationsMatch[1] : null
          };
        }
      }
    }

    // Pattern 2: Content ending with .", "citations": [...]
    let citationsPattern = /^(.*?)",\s*"citations"\s*:\s*(\[.*?\])/s;
    let match = content.match(citationsPattern);

    if (match) {
      return {
        content: match[1],
        citations: match[2]
      };
    }

    // Pattern 3: Full JSON object { "response": "...", "citations": [...], "confidence": ... } (not starting with {"response":)
    if (content.includes('"citations":') && content.includes('"confidence":')) {
      try {
        const fullData = JSON.parse(content);
        if (fullData.response && fullData.citations) {
          return {
            content: fullData.response,
            citations: JSON.stringify(fullData.citations)
          };
        }
      } catch (e) {
        // If full JSON parse fails, try regex extraction
        const responseMatch = content.match(
          /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/
        );
        const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);

        if (responseMatch && citationsMatch) {
          return {
            content: responseMatch[1]
              .replace(/\\\\/g, "\\")
              .replace(/\\"/g, '"')
              .replace(/\\n/g, "\n")
              .replace(/\\r/g, "\r")
              .replace(/\\t/g, "\t"),
            citations: citationsMatch[1]
          };
        }
      }
    }

    // Pattern 4: Content with escaped JSON citations
    if (content.includes('\\", \\"citations\\":')) {
      const citationsIndex = content.indexOf('", "citations":');
      if (citationsIndex > -1) {
        const extractedContent = content.substring(0, citationsIndex);
        const remainingContent = content.substring(citationsIndex);
        const citationsMatch = remainingContent.match(
          /"citations"\s*:\s*(\[.*?\])/s
        );

        if (citationsMatch) {
          return {
            content: extractedContent,
            citations: citationsMatch[1]
          };
        }
      }
    }

    // Pattern 5: Any content ending with JSON-like structures
    if (
      content.includes('"citations"') &&
      (content.includes('"url":') ||
        content.includes('"relevance_score":') ||
        content.includes('"confidence":'))
    ) {
      // Try to find where the actual content ends and JSON begins
      // Look for patterns like: text.", "citations": or text." } or similar
      const patterns = [
        /^(.*?)",\s*"citations"\s*:\s*(\[.*?\])/s,
        /^(.*?)\.",\s*"citations"\s*:\s*(\[.*?\])/s,
        /^(.*?)\."[^"]*$/s
      ];

      for (const pattern of patterns) {
        const match = content.match(pattern);
        if (match) {
          const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);
          return {
            content: match[1],
            citations: citationsMatch ? citationsMatch[1] : null
          };
        }
      }
    }

    // No pattern matched, return original content
    return {
      content: content,
      citations: null
    };
  };

  // Extract unique providers and models from messages
  const getProvidersAndModels = useCallback(() => {
    if (!thread?.messages)
      return { providers: [], models: [], combinations: [] };

    const providers = new Set();
    const models = new Set();
    const combinations = new Set();

    thread.messages.forEach((message) => {
      if (message.role === "assistant") {
        if (message.provider) {
          providers.add(message.provider);
        }
        if (message.model) {
          models.add(message.model);
        }
        if (message.provider && message.model) {
          combinations.add(`${message.provider} • ${message.model}`);
        }
      }
    });

    return {
      providers: Array.from(providers).sort(),
      models: Array.from(models).sort(),
      combinations: Array.from(combinations).sort()
    };
  }, [thread?.messages]);

  // Ensure first response is active when tabs become visible
  useEffect(() => {
    if (thread?.messages) {
      const groups = groupMessages();

      setActiveTabs((prevActiveTabs) => {
        const updates = { ...prevActiveTabs };
        let hasChanges = false;

        groups.forEach((group) => {
          // Check if this group should show tabs and doesn't have an active tab
          if (
            (group.botResponses.length > 1 ||
              group.botResponses.some((r) => r.model || r.provider)) &&
            !updates[group.id] &&
            group.botResponses.length > 0
          ) {
            updates[group.id] = group.botResponses[0].id;
            hasChanges = true;
          }
        });

        return hasChanges ? updates : prevActiveTabs;
      });
    }
  }, [thread?.messages, groupMessages]);

  return (
    <>
      <MainContent $isOpen={isSidePanelOpen}>
        <MessagesContainer>
          <PageHeader
            title={thread ? `Chat with ${thread.metadata.botName}` : "Chat"}
            subtitle={thread?.metadata?.username}
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <ChatContainer>
            {loading ? (
              <LoadingState>Loading thread...</LoadingState>
            ) : error ? (
              <ErrorState>{error}</ErrorState>
            ) : thread && thread.messages && thread.messages.length > 0 ? (
              <MessagesList>
                <MessagesWrapper>
                  {groupMessages().map((group, groupIndex) => {
                    // Check if this is the initial welcome message (first group with no user message)
                    const isWelcomeMessage =
                      groupIndex === 0 && !group.userMessage;

                    return (
                      <div
                        key={group.id}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem"
                        }}
                      >
                        {/* User message */}
                        {group.userMessage && (
                          <Message $type={group.userMessage.role}>
                            <MessageContent $type={group.userMessage.role}>
                              <MessageText>
                                {processMessageContent(
                                  group.userMessage.content
                                )}
                              </MessageText>
                            </MessageContent>
                            <MessageMetadata $type={group.userMessage.role}>
                              {formatMessageTime(group.userMessage.createdAt)}
                            </MessageMetadata>
                          </Message>
                        )}

                        {/* Bot responses with tabs */}
                        {group.botResponses.length > 0 && (
                          <Message $type="assistant">
                            <MessageContent $type="assistant">
                              {/* Response tabs - only show if there are multiple models */}
                              {(() => {
                                const uniqueModels = new Set();
                                const uniqueProviders = new Set();

                                group.botResponses.forEach((response) => {
                                  if (response.model)
                                    uniqueModels.add(response.model);
                                  if (response.provider)
                                    uniqueProviders.add(response.provider);
                                });

                                const hasMultipleModels =
                                  uniqueModels.size > 1 ||
                                  uniqueProviders.size > 1 ||
                                  group.botResponses.length > 1;

                                return (
                                  hasMultipleModels && (
                                    <AdminResponseTabs
                                      responses={group.botResponses}
                                      activeTabId={activeTabs[group.id]}
                                      onTabClick={handleTabSwitch}
                                      groupId={group.id}
                                    />
                                  )
                                );
                              })()}

                              {/* Active response content */}
                              {(() => {
                                const activeResponse =
                                  group.botResponses.find(
                                    (r) => r.id === activeTabs[group.id]
                                  ) || group.botResponses[0];

                                // Process content and citations from server data
                                const rawContent = activeResponse.content || "";
                                let processedContent = rawContent;
                                let parsedCitations = activeResponse.citations;

                                const parsed =
                                  parseContentAndCitations(rawContent);
                                processedContent = parsed.content;

                                // Parse citations if found
                                if (parsed.citations) {
                                  try {
                                    parsedCitations = JSON.parse(
                                      parsed.citations
                                    );
                                  } catch (citationError) {
                                    console.warn(
                                      "Failed to parse citations:",
                                      citationError
                                    );
                                    parsedCitations = activeResponse.citations; // Fallback to original
                                  }
                                } else if (
                                  typeof parsedCitations === "string"
                                ) {
                                  // Parse citations if they're in string format (fallback)
                                  try {
                                    parsedCitations =
                                      JSON.parse(parsedCitations);
                                  } catch (e) {
                                    console.warn(
                                      "Failed to parse citations:",
                                      e
                                    );
                                    parsedCitations = [];
                                  }
                                }

                                // Convert any remaining literal \n characters to actual newlines
                                processedContent = processedContent.replace(
                                  /\\n/g,
                                  "\n"
                                );

                                return (
                                  <MessageText>
                                    {processMessageContent(
                                      processedContent,
                                      parsedCitations,
                                      activeResponse.id,
                                      activeResponse.citationsLoading,
                                      activeResponse.model
                                    )}
                                  </MessageText>
                                );
                              })()}
                            </MessageContent>

                            {/* Metadata for active response */}
                            {(() => {
                              const activeResponse =
                                group.botResponses.find(
                                  (r) => r.id === activeTabs[group.id]
                                ) || group.botResponses[0];

                              return (
                                <MessageMetadata $type="assistant">
                                  {formatMessageTime(activeResponse.createdAt)}
                                  {/* Hide model metadata for welcome messages only */}
                                  {!isWelcomeMessage && (
                                    <>
                                      {activeResponse.provider &&
                                        activeResponse.model
                                        ? ` • ${activeResponse.provider} • ${activeResponse.model}`
                                        : activeResponse.model ||
                                          activeResponse.provider
                                        ? ` • ${
                                            activeResponse.model ||
                                            activeResponse.provider
                                          }`
                                        : ""}
                                    </>
                                  )}
                                </MessageMetadata>
                              );
                            })()}

                            {/* Rating and feedback for active response */}
                            {(() => {
                              const activeResponse =
                                group.botResponses.find(
                                  (r) => r.id === activeTabs[group.id]
                                ) || group.botResponses[0];

                              return (
                                (activeResponse.rating !== null ||
                                  activeResponse.feedback) && (
                                  <RatingContainer>
                                    {activeResponse.rating !== null && (
                                      <RatingButtons>
                                        {activeResponse.rating === true && (
                                          <RatingButton
                                            $type="up"
                                            $selected={true}
                                          >
                                            👍
                                          </RatingButton>
                                        )}
                                        {activeResponse.rating === false && (
                                          <RatingButton
                                            $type="down"
                                            $selected={true}
                                          >
                                            👎
                                          </RatingButton>
                                        )}
                                      </RatingButtons>
                                    )}

                                    {activeResponse.feedback && (
                                      <SavedFeedbackDisplay>
                                        {activeResponse.feedback}
                                      </SavedFeedbackDisplay>
                                    )}
                                  </RatingContainer>
                                )
                              );
                            })()}

                            {/* Citations for active response */}
                            {(() => {
                              const activeResponse =
                                group.botResponses.find(
                                  (r) => r.id === activeTabs[group.id]
                                ) || group.botResponses[0];

                              // Parse citations using same logic as content processing above
                              const rawContent = activeResponse.content || "";
                              let parsedCitations = activeResponse.citations;

                              // Use the same parseContentAndCitations function as above
                              const parsed =
                                parseContentAndCitations(rawContent);

                              // Parse citations if found
                              if (parsed.citations) {
                                try {
                                  parsedCitations = JSON.parse(
                                    parsed.citations
                                  );
                                } catch (citationError) {
                                  console.warn(
                                    "Citations section: Failed to parse citations:",
                                    citationError
                                  );
                                  parsedCitations = activeResponse.citations; // Fallback to original
                                }
                              } else if (typeof parsedCitations === "string") {
                                // Parse citations if they're in string format (fallback)
                                try {
                                  parsedCitations = JSON.parse(parsedCitations);
                                } catch (e) {
                                  console.warn("Failed to parse citations:", e);
                                  parsedCitations = [];
                                }
                              }

                              return (
                                <Citations
                                  citations={parsedCitations}
                                  citationsLoading={
                                    activeResponse.citationsLoading
                                  }
                                  responseId={activeResponse.id}
                                  modelname={activeResponse.model}
                                />
                              );
                            })()}
                          </Message>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </MessagesWrapper>
              </MessagesList>
            ) : (
              <EmptyState>
                <h4>No Messages</h4>
                <p>This thread has no messages.</p>
              </EmptyState>
            )}
          </ChatContainer>
        </MessagesContainer>
      </MainContent>

      <SidePanel $isOpen={isSidePanelOpen}>
        {thread?.metadata && (
          <>
            <InfoSection>
              <InfoLabel>Thread ID</InfoLabel>
              <InfoValue>{thread.metadata.threadId}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>Username</InfoLabel>
              <InfoValue>{thread.metadata.username || "Unknown"}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>Bot</InfoLabel>
              <InfoValue>{thread.metadata.botName || "Unknown"}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>Type</InfoLabel>
              <InfoValue>{thread.metadata.botType || "Unknown"}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>Models</InfoLabel>
              {(() => {
                const { models } = getProvidersAndModels();
                return models.length > 0 ? (
                  models.map((model, index) => (
                    <InfoValue
                      key={index}
                      style={{
                        marginBottom:
                          index < models.length - 1 ? "0.25rem" : "0"
                      }}
                    >
                      {model}
                    </InfoValue>
                  ))
                ) : (
                  <InfoValue>None</InfoValue>
                );
              })()}
            </InfoSection>

            <InfoSection>
              <InfoLabel>Chat started</InfoLabel>
              <InfoValue>{formatDate(thread.metadata.startedAt)}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>Most recent message</InfoLabel>
              <InfoValue>{formatDate(thread.metadata.lastMessageAt)}</InfoValue>
            </InfoSection>

            {thread.metadata.durationSeconds > 0 && (
              <InfoSection>
                <InfoLabel>Duration</InfoLabel>
                <InfoValue>
                  {formatDuration(thread.metadata.durationSeconds)}
                </InfoValue>
              </InfoSection>
            )}

            <InfoSection>
              <InfoLabel>Number of messages</InfoLabel>
              <InfoValue>
                {thread.metadata.totalMessages} ({thread.metadata.userMessages}{" "}
                user, {thread.metadata.assistantMessages} bot)
              </InfoValue>
            </InfoSection>

            <InfoSection>
              <Button onClick={handleDeleteThread} variant="danger" $fullWidth>
                Delete thread
              </Button>
            </InfoSection>
          </>
        )}
      </SidePanel>

      <ToggleButton
        onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
        variant="secondary"
      >
        {isSidePanelOpen ? "Hide Info" : "Show Info"}
      </ToggleButton>
    </>
  );
}

export default AdminThread;
